export const SOLLIO_DELIVERY_ADDRESS_SELECTION_REQUIRED_CODE = "sollio_delivery_address_selection_required";
export const SOLLIO_CITY_FALLBACK_LABEL = "Ville non disponible";
export const SOLLIO_DELIVERY_ADDRESS_DIALOG_FALLBACK_TITLE = "Adresses de livraison";

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
}

function resolveDisplayValue(value, fallback = "—") {
  const normalizedValue = normalizeText(value);
  return normalizedValue || fallback;
}

export function normalizeSollioDeliveryAddressOption(option) {
  if (!option || typeof option !== "object" || Array.isArray(option)) {
    return {
      correspondence_id: "",
      client_code: "—",
      client_name: "—",
      correspondence_name: "—",
      city_label: SOLLIO_CITY_FALLBACK_LABEL,
    };
  }

  return {
    correspondence_id: option.correspondence_id ?? "",
    client_code: resolveDisplayValue(option.client_code),
    client_name: resolveDisplayValue(option.client_name),
    correspondence_name: resolveDisplayValue(option.correspondence_name),
    city_label: resolveDisplayValue(option.city_label, SOLLIO_CITY_FALLBACK_LABEL),
  };
}

export function formatSollioDeliveryAddressOption(option) {
  const normalizedOption = normalizeSollioDeliveryAddressOption(option);
  return `${normalizedOption.client_code} · ${normalizedOption.client_name} · ${normalizedOption.correspondence_name} · ${normalizedOption.city_label}`;
}

function normalizeOrderOptions(order, options) {
  const localOptions = Array.isArray(order?.options) ? order.options : null;
  const availableOptions = localOptions || options;
  const normalizedOptions = Array.isArray(availableOptions)
    ? availableOptions.map(normalizeSollioDeliveryAddressOption)
    : [];
  // The backend sends one top-level option list shared by every targeted
  // source row. Keep it intact: two distinct correspondences may share a city,
  // and the source city is context rather than a client-side filter.
  return normalizedOptions;
}

export function getSollioDeliveryAddressOrderKey(order, index = 0) {
  const sourceRowNumber = order?.source_row_number;
  return sourceRowNumber == null || sourceRowNumber === ""
    ? `row-${index}`
    : String(sourceRowNumber);
}

function hasValidSollioDeliveryAddressSelection(order, selection) {
  const normalizedSelection = selection === undefined || selection === null ? "" : String(selection).trim();
  const orderOptions = Array.isArray(order?.options) ? order.options : [];
  return normalizedSelection !== "" && orderOptions.some(
    (option) => String(option?.correspondence_id ?? "").trim() === normalizedSelection,
  );
}

export function normalizeSollioDeliveryAddressOrder(order, options = [], index = 0) {
  const sourceCity = resolveDisplayValue(order?.source_city, SOLLIO_CITY_FALLBACK_LABEL);
  const destinationLabel = resolveDisplayValue(order?.destination_label, SOLLIO_DELIVERY_ADDRESS_DIALOG_FALLBACK_TITLE);
	const normalizedOptions = normalizeOrderOptions({ ...order, source_city: sourceCity }, options);
	const rememberedChoice = order?.remembered_choice && typeof order.remembered_choice === "object"
		? {
			...normalizeSollioDeliveryAddressOption(order.remembered_choice),
			updated_at: normalizeText(order.remembered_choice.updated_at),
		}
		: null;

  return {
    source_row_number: order?.source_row_number ?? index + 1,
		rule_id: normalizeText(order?.rule_id),
		source_row_numbers: Array.isArray(order?.source_row_numbers) ? order.source_row_numbers : [],
    destination_family: normalizeText(order?.destination_family),
    destination_label: destinationLabel,
    source_destination: resolveDisplayValue(order?.source_destination, destinationLabel),
    purchase_order: resolveDisplayValue(order?.purchase_order),
    delivery_date: resolveDisplayValue(order?.delivery_date),
    product: resolveDisplayValue(order?.product),
    quantity_tons: resolveDisplayValue(order?.quantity_tons),
    silo: resolveDisplayValue(order?.silo),
    source_city: sourceCity,
		options: normalizedOptions,
		remembered_choice: rememberedChoice,
		requires_selection: typeof order?.requires_selection === "boolean"
			? order.requires_selection
			: rememberedChoice === null,
  };
}

export function resolveSollioDeliveryAddressDialogTitle(challenge) {
  if (!challenge || !Array.isArray(challenge.orders) || challenge.orders.length === 0) {
    return SOLLIO_DELIVERY_ADDRESS_DIALOG_FALLBACK_TITLE;
  }

  const destinationLabels = [...new Set(
    challenge.orders
      .map((order) => normalizeText(order?.destination_label))
      .filter(Boolean),
  )];
  return destinationLabels.length === 1
    ? destinationLabels[0]
    : SOLLIO_DELIVERY_ADDRESS_DIALOG_FALLBACK_TITLE;
}

export function groupSollioDeliveryAddressOrdersByFamily(challenge, selections = {}) {
  if (!challenge || !Array.isArray(challenge.orders)) {
    return [];
  }

  const groups = [];
  const groupsByFamily = new Map();

  challenge.orders.forEach((order, orderIndex) => {
    const destinationFamily = normalizeText(order?.destination_family);
    const destinationLabel = resolveDisplayValue(
      order?.destination_label,
      SOLLIO_DELIVERY_ADDRESS_DIALOG_FALLBACK_TITLE,
    );
    const familyKey = destinationFamily || `label:${destinationLabel.toLowerCase()}`;
    let group = groupsByFamily.get(familyKey);

    if (!group) {
      group = {
        familyKey,
        destinationFamily,
        destinationLabel,
        entries: [],
        completedCount: 0,
        totalCount: 0,
        isBlocked: false,
      };
      groupsByFamily.set(familyKey, group);
      groups.push(group);
    }

    const orderKey = getSollioDeliveryAddressOrderKey(order, orderIndex);
    const orderOptions = Array.isArray(order?.options) ? order.options : [];
    group.entries.push({ order, orderIndex, orderKey });
    group.totalCount += 1;
    group.isBlocked = group.isBlocked || orderOptions.length === 0;
    if (hasValidSollioDeliveryAddressSelection(order, selections[orderKey])) {
      group.completedCount += 1;
    }
  });

  return groups.map((group) => ({
    ...group,
    isComplete: group.totalCount > 0 && group.completedCount === group.totalCount,
  }));
}

export function normalizeSollioDeliveryAddressChallenge(payload) {
  if (
    !payload
    || typeof payload !== "object"
    || payload.code !== SOLLIO_DELIVERY_ADDRESS_SELECTION_REQUIRED_CODE
  ) {
    return null;
  }

  const normalizedOptions = Array.isArray(payload.options)
    ? payload.options.map(normalizeSollioDeliveryAddressOption)
    : [];
  const normalizedOrders = Array.isArray(payload.orders)
    ? payload.orders.map((order, index) => normalizeSollioDeliveryAddressOrder(order, normalizedOptions, index))
    : [];
	const orders = normalizedOrders.filter((order) => order.requires_selection !== false);

  return {
    code: SOLLIO_DELIVERY_ADDRESS_SELECTION_REQUIRED_CODE,
    message: resolveDisplayValue(payload.message, "Sélectionnez une adresse de livraison pour chaque commande."),
    orders,
    options: normalizedOptions,
		reused_selection_count: Number.isFinite(Number(payload.reused_selection_count))
			? Number(payload.reused_selection_count)
			: 0,
		unresolved_selection_count: Number.isFinite(Number(payload.unresolved_selection_count))
			? Number(payload.unresolved_selection_count)
			: orders.filter((order) => order.requires_selection).length,
  };
}

export function buildInitialSollioDeliveryAddressSelections(challenge) {
	if (!challenge || !Array.isArray(challenge.orders)) {
		return {};
	}

	return challenge.orders.reduce((result, order, index) => {
		const rememberedCorrespondenceId = order?.remembered_choice?.correspondence_id;
		if (hasValidSollioDeliveryAddressSelection(order, rememberedCorrespondenceId)) {
			result[getSollioDeliveryAddressOrderKey(order, index)] = rememberedCorrespondenceId;
		}
		return result;
	}, {});
}

export function isSollioDeliveryAddressSelectionComplete(challenge, selections = {}) {
  if (!challenge || !Array.isArray(challenge.orders) || challenge.orders.length === 0) {
    return false;
  }

  return challenge.orders.every((order, index) => {
    const selection = selections[getSollioDeliveryAddressOrderKey(order, index)];
    return hasValidSollioDeliveryAddressSelection(order, selection);
  });
}

export function buildSollioDeliveryAddressSelections(challenge, selections = {}) {
  if (!challenge || !Array.isArray(challenge.orders)) {
    return [];
  }

  return challenge.orders.reduce((result, order, index) => {
    const correspondenceId = selections[getSollioDeliveryAddressOrderKey(order, index)];
		if (!hasValidSollioDeliveryAddressSelection(order, correspondenceId)) {
      return result;
    }
		const normalizedCorrespondenceId = String(correspondenceId).trim();
		const rememberedCorrespondenceId = order?.remembered_choice?.correspondence_id == null
			? ""
			: String(order.remembered_choice.correspondence_id).trim();
		if (rememberedCorrespondenceId && normalizedCorrespondenceId === rememberedCorrespondenceId) {
			return result;
		}

    result.push({
      source_row_number: order.source_row_number,
			correspondence_id: normalizedCorrespondenceId,
    });
    return result;
  }, []);
}

export function resetSollioDeliveryAddressSelectionState() {
  return {
    challenge: null,
    isOpen: false,
    selections: {},
  };
}
